import React, { useEffect } from "react";

import { Box, Fade, makeStyles } from "@material-ui/core";
import { Save as SaveIcon } from "@material-ui/icons";

import { AreaDiscounts, AreaPricesCard } from "@hubcapp/components";
import { DistrictCoverage } from "@hubcapp/components";
import { ProductCategoryType } from "@hubcapp/types";
import Button from "@ds-developer/button";
import Content from "@ds-developer/content";
import PageHeader from "@ds-developer/page-header";

import { AddNewAreaProps } from "./types";
import { getAreaPageTitle } from "./helpers";
import AreaPostcodeSearch from "./components/AreaPostcodeSearch";

const useStyles = makeStyles(theme => ({
  nextButton: {
    alignSelf: "center",
    [theme.breakpoints.down("sm")]: { marginTop: theme.spacing(2) },
  },
  saveButton: {
    alignSelf: "center",
    [theme.breakpoints.up("md")]: { marginLeft: theme.spacing(6) },
  },
  postcodeSearch: {
    maxWidth: 400,
  },
  nextButtonWrapper: {
    marginTop: theme.spacing(5),
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      justifyContent: "center",
    },
  },
  saveButtonWrapper: {
    marginTop: theme.spacing(5),
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      justifyContent: "center",
      flexDirection: "column-reverse",
    },
  },
}));

const AddNewArea: React.FC<AddNewAreaProps> = ({
  stage,
  setStage,
  loading,
  id,
  name,
  areaCode,
  canEditDistricts,
  onDistrictsChange,
  districts,
  setInitialRegion,
  discard,
  onDiscard,
  areaPrices,
  onPriceChange,
  canEditPrices,
  discounts,
  onDiscountsChange,
  onSubmit,
  submitting,
  propositionId,
  productCategoryId,
  productCategoryType,
}) => {
  const classes = useStyles();
  const nextButtonRef: React.RefObject<any> = React.createRef();
  useEffect(() => {
    if (!nextButtonRef.current) return;
    nextButtonRef.current.scrollIntoView({ behavior: "smooth" });
  }, [stage]);

  return (
    <Content>
      <PageHeader
        title={`Add a new ${getAreaPageTitle(productCategoryType)} area`}
        breadcrumbs={[
          {
            title: "Home",
            link: "/",
          },
          {
            title: "Areas & prices",
            link: "/settings/areas-and-prices",
          },
          {
            title: "Add an area",
          },
        ]}
      />
      {}
      <Box className={classes.postcodeSearch} mt={3}>
        <AreaPostcodeSearch
          propositionId={propositionId}
          productCategoryId={productCategoryId}
          discard={discard}
          onChange={setInitialRegion}
        />
      </Box>
      {stage >= 2 && (
        <Fade timeout={500} in={stage >= 2}>
          <Box mt={3}>
            <DistrictCoverage
              loading={loading}
              areaId={id}
              title="1. Define your new area"
              areaName={name}
              areaShortCode={areaCode}
              canEdit={canEditDistricts}
              onDistrictsUpdate={onDistrictsChange}
              instructorDistricts={districts}
            />
          </Box>
        </Fade>
      )}
      {stage >= 3 && (
        <Fade timeout={500} in={stage >= 2}>
          <Box mt={3}>
            <AreaPricesCard
              loading={loading}
              areaCode={areaCode}
              areaName={name}
              title={`2. Set your prices for ${name}`}
              editable={canEditPrices}
              prices={areaPrices}
              onPriceChange={onPriceChange}
            />
          </Box>
        </Fade>
      )}
      {stage >= 4 &&
        productCategoryId &&
        discounts &&
        productCategoryType !== ProductCategoryType.Motability && (
          <Fade timeout={500} in={stage >= 3}>
            <Box mt={3}>
              {discounts && (
                <AreaDiscounts
                  title={`3. Decide what discounts you want for ${name}`}
                  areaId={id}
                  instructorDiscounts={discounts}
                  loading={loading}
                  onChange={onDiscountsChange}
                  propositionId={propositionId}
                  productCategoryId={productCategoryId}
                />
              )}
            </Box>
          </Fade>
        )}
      {(stage > 1 && stage < 3 && discounts) ||
      (stage == 3 && productCategoryType !== ProductCategoryType.Motability) ? (
        <div ref={nextButtonRef} className={classes.nextButtonWrapper}>
          <Button
            color="secondary"
            data-test="next-button"
            variant="contained"
            size="large"
            className={classes.nextButton}
            onClick={() => setStage(stage + 1)}
          >
            NEXT
          </Button>
        </div>
      ) : stage >= 4 ||
        (!discounts && stage >= 3) ||
        (stage >= 3 &&
          productCategoryType === ProductCategoryType.Motability) ? (
        <div ref={nextButtonRef} className={classes.saveButtonWrapper}>
          <Button
            color="default"
            data-test="discard-button"
            variant="outlined"
            size="large"
            className={classes.nextButton}
            onClick={onDiscard}
          >
            DISCARD
          </Button>
          <Button
            color="secondary"
            data-test="add-new-area-button"
            variant="contained"
            size="large"
            className={classes.saveButton}
            onClick={onSubmit}
            disabled={submitting}
            startIcon={<SaveIcon />}
          >
            SAVE YOUR NEW AREA
          </Button>
        </div>
      ) : null}
    </Content>
  );
};

export default AddNewArea;
